/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/* full bleed が 100vi から差し引く値。ずれると横スクロールが出る */
const updateScrollbarWidth = () => {
  const scrollbarWidth =
    window.innerWidth - document.documentElement.clientWidth + "px";
  const root = document.documentElement;

  // ResizeObserver のループ防止
  if (root.style.getPropertyValue("--scrollbar-width") === scrollbarWidth) {
    return;
  }

  root.style.setProperty("--scrollbar-width", scrollbarWidth);
};

updateScrollbarWidth();
new ResizeObserver(updateScrollbarWidth).observe(document.documentElement);

const userAgent = navigator.userAgent;

/**
 * Appify アプリ内ブラウザ
 */
const isUserAgentAppify = /Appify/i.test(userAgent);
function isAppifyHidden() {
  if (isUserAgentAppify) {
    const footerSpMenu = document.getElementsByClassName("footer-sp-menu")[0];
    if (footerSpMenu) {
      footerSpMenu.style.display = "none";
    }
  }

  const appifyHiddenElements = document.querySelectorAll(".c_js-appify--hidden");
  if (isUserAgentAppify && appifyHiddenElements.length > 0) {
    appifyHiddenElements.forEach((element) => {
      element.style.display = "none";
    });
  }
}

// スケルトンスクリーン: 読み込み完了で data-js="skeleton-loading" を hidden 化
function initSkeletonLoader() {
  const skeletons = document.querySelectorAll('[data-js="skeleton-loading"]');
  if (skeletons.length === 0) return;

  const hide = () => skeletons.forEach((el) => el.setAttribute("hidden", ""));

  if (document.readyState === "complete") {
    hide();
  } else {
    window.addEventListener("load", hide, { once: true });
  }
}

// カードコンテンツのリンク要素
const initializeCardEvents = () => {
  const initializeCard = (card) => {
    card.addEventListener("click", handleCardClick);
    card.querySelectorAll("a").forEach((link) => {
      link.addEventListener("mouseover", () => handleLinkHover(card, true));
      link.addEventListener("mouseout", () => handleLinkHover(card, false));
    });
  };

  const handleCardClick = (event) => {
    if (event.target.closest("a")) {
      return;
    }

    const card = event.currentTarget;
    const href = card.getAttribute("data-href");
    if (!href) return;

    const blank = card.hasAttribute("data-blank");
    if (blank) {
      window.open(href, "_blank");
    } else {
      window.location.href = href;
    }
  };

  const handleLinkHover = (card, isHovered) => {
    if (isHovered) {
      card.setAttribute("data-link-hovered", "true");
    } else {
      card.removeAttribute("data-link-hovered");
    }
  };

  const initializeCards = () => {
    const cards = document.querySelectorAll(".card-item-event");
    if (cards.length === 0) return;

    cards.forEach((card) => {
      initializeCard(card);
    });
  };

  return { initializeCards };
};
const cardEvents = initializeCardEvents();
// アコーディオンの展開
function initializeAccordion() {
  // ページ内にアコーディオンが存在する場合のみ初期化
  if (document.querySelector("details.accordion")) {
    setupToggleDetailsAnimation();
  }
}

function setupToggleDetailsAnimation() {
  // 対象要素がない場合は早期リターン
  const detailsElements = document.querySelectorAll("details.accordion");
  if (detailsElements.length === 0) return;

  // イベント委譲を使用：ページ全体で1回だけリスナー登録
  document.addEventListener("click", (e) => {
    const summary = e.target.closest("details.accordion summary");
    if (!summary) return;

    const detailsElem = summary.closest("details.accordion");
    const icon = summary.querySelector("span.icon");
    const openedIcon = summary.querySelector("span.icon.opened");
    const content = detailsElem.querySelector(".accordion__content");

    // 必要な要素がない場合は処理をスキップ
    if (!icon || !openedIcon || !content) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    if (detailsElem.open) {
      closeAccordion(detailsElem, content, icon, openedIcon);
    } else {
      openAccordion(detailsElem, content, icon, openedIcon);
    }
  });
}

function closeAccordion(detailsElem, content, icon, openedIcon) {
  const closeAnimation = content.animate(
    {
      opacity: [1, 0],
      height: [`${content.offsetHeight}px`, "0"],
    },
    {
      duration: 300,
      easing: "ease-in",
    }
  );

  openedIcon.animate(
    { rotate: ["180deg", "0deg"] },
    {
      duration: 300,
      easing: "ease-in",
      fill: "forwards",
    }
  );

  closeAnimation.onfinish = () => {
    detailsElem.removeAttribute("open");
    openedIcon.style.display = "none";
    icon.style.display = "block";
  };
}

function openAccordion(detailsElem, content, icon, openedIcon) {
  detailsElem.setAttribute("open", "true");
  openedIcon.style.display = "none";

  const openAnimation = content.animate(
    {
      opacity: [0, 1],
      height: ["0", `${content.offsetHeight}px`],
    },
    {
      duration: 300,
      easing: "ease-in",
    }
  );

  icon.animate(
    { rotate: ["0deg", "180deg"] },
    {
      duration: 300,
      easing: "ease-in",
      fill: "forwards",
    }
  );

  openAnimation.onfinish = () => {
    openedIcon.style.display = "block";
    icon.style.display = "none";
  };
}

// お気に入り
// カート削除だけは応答を待たずに reload するため keepalive が要る。
// 未対応ブラウザでは登録が破棄されるので、呼び出し側が await にフォールバックする
window.supportsFetchKeepalive = (() => {
  try {
    return new Request("/", { method: "POST", body: "", keepalive: true })
      .keepalive === true;
  } catch {
    return false;
  }
})();

window.submitFavorite = async (url, handle, variant) => {
  const fetchOptions = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ handle, variant }),
    keepalive: true,
  };
  const response = await fetch(url, fetchOptions);
  if (!response.ok) throw new Error(`favorite api responded ${response.status}`);

  // この API は失敗も HTTP 200 で返し、本文の error で示す
  const result = await response.json();
  if (result.error) throw new Error(result.message || "favorite api returned error");
};

function initializeFavorites() {
  const addText = window.favoriteTextAdd;
  const addedText = window.favoriteTextAdded;
  const variantIds = new Set(window.favoriteVariantIds);
  const handleCounts = {};
  const handlesSet = new Set(window.favoriteHandles);
  const allVariantIds = window.productVariantIds || [];

  // handleのvariantをカウント
  window.favoriteVariantIds.forEach((vid, i) => {
    const h = window.favoriteHandles[i];
    handleCounts[h] = (handleCounts[h] || 0) + 1;
  });

  // 一覧・商品ページを判定
  function isListButton(btn) {
    return btn.closest(".c_c-favorite-app-list") !== null;
  }
  function isProductButton(btn) {
    return btn.closest(".c_c-favorite-app-item") !== null;
  }

  function updateAllListButtons() {
    document
      .querySelectorAll(".c_c-favorite-app-list .mxpr-fav-add")
      .forEach((btn) => {
        const h = btn.dataset.handle;
        const fav = handlesSet.has(h);
        btn.classList.toggle("is-added", fav);
        btn.setAttribute("aria-label", fav ? addedText : addText);
      });
  }

  function updateButton(btn, isFav) {
    if (isListButton(btn)) {
      btn.classList.toggle("is-added", isFav);
      btn.setAttribute("aria-label", isFav ? addedText : addText);
    }
    if (isProductButton(btn)) {
      btn.classList.toggle("is-added", isFav);
      const span = btn.querySelector("span");
      if (span) span.textContent = isFav ? addedText : addText;
      btn.setAttribute("aria-label", isFav ? addedText : addText);
      updateAllListButtons();
    }
  }

  function updateMatchItemButton(variantId, isFav) {
    document
      .querySelectorAll(".c_c-favorite-app-item .mxpr-fav-add")
      .forEach((itemBtn) => {
        if (String(itemBtn.dataset.variantId) === String(variantId)) {
          updateButton(itemBtn, isFav);
        }
      });
  }

  async function onFavClick(e, btn) {
    e.preventDefault();
    if (btn.dataset.saving === "true") return;

    const handle = btn.dataset.handle;
    const vid = Number(btn.dataset.variantId);
    const cid = btn.dataset.customerId;
    if (!cid) return;
    // 連続クリック対策
    btn.dataset.saving = "true";
    btn.classList.add("is-saving");

    const wasFav = variantIds.has(vid);
    const originalText = wasFav ? addedText : addText;
    const nextFav = !wasFav;
    const nextStatus = nextFav ? addedText : addText;

    const span = btn.querySelector("span");
    if (span) span.textContent = nextStatus;
    btn.setAttribute("aria-label", nextStatus);

    try {
      if (wasFav) {
        await window.submitFavorite(
          "/tools/prime-review/api/removeFavorite",
          handle,
          vid
        );
        variantIds.delete(vid);
        const stillHas = allVariantIds.some((v) => variantIds.has(v));
        if (!stillHas) {
          delete handleCounts[handle];
          handlesSet.delete(handle);
        } else {
          handleCounts[handle]--;
        }
      } else {
        await window.submitFavorite(
          "/tools/prime-review/api/addFavorite",
          handle,
          vid
        );
        variantIds.add(vid);
        handleCounts[handle] = (handleCounts[handle] || 0) + 1;
        handlesSet.add(handle);
      }
      updateButton(btn, nextFav);
      if (isListButton(btn)) {
        updateMatchItemButton(vid, nextFav);
        updateAllListButtons();
      }
    } catch (err) {
      console.error("お気に入りの追加に失敗しました", err);
      if (span) span.textContent = originalText;
      btn.setAttribute("aria-label", originalText);
    } finally {
      btn.classList.remove("is-saving");
      delete btn.dataset.saving;
    }
  }

  document.addEventListener("click", (e) => {
    const btn = e.target.closest(".mxpr-fav-add");
    if (btn) onFavClick(e, btn);
  });

  // 商品ページのバリアント変更
  document.addEventListener("variant:changed", (e) => {
    const v = e.detail.variant;
    if (!v) return;
    document
      .querySelectorAll(".c_c-favorite-app-item .mxpr-fav-add")
      .forEach((btn) => {
        btn.dataset.variantId = v.id;
        updateButton(btn, variantIds.has(v.id));
      });
  });

  updateAllListButtons();
}

// OmniHub スクリプトの遅延読み込み
function initializeLazyLoadedScripts() {
  let scriptsLoaded = false;

  function loadOmniHubScripts() {
    if (scriptsLoaded) return;
    scriptsLoaded = true;

    // OmniHub関連要素が存在する場合のみ読み込み
    const omnihubElements = [
      "#omni-hub-account",
      "#omni-hub-cart",
      "#omni-hub-pass",
      "#omni-hub-qrcode",
      "#omni-hub-barcode",
    ];

    const hasOmnihubElement = omnihubElements.some((selector) =>
      document.querySelector(selector)
    );

    if (hasOmnihubElement) {
      // 必要なOmniHubスクリプトを動的読み込み
      const scripts = [
        "omnihub_account.js",
        "omnihub_cart.js",
        "omnihub_pass.js",
        "omnihub_qrcode.js",
        "omnihub_barcode.js",
      ];

      scripts.forEach((scriptName) => {
        const correspondingElement = document.querySelector(
          `#omni-hub-${scriptName.replace("omnihub_", "").replace(".js", "")}`
        );
        if (correspondingElement) {
          const script = document.createElement("script");
          script.src = `/assets/${scriptName}`;
          script.defer = true;
          document.head.appendChild(script);
        }
      });
    }
  }

  // 遅延読み込みトリガー設定
  // ユーザーが初回スクロールした時
  document.addEventListener("scroll", loadOmniHubScripts, { once: true });
  setTimeout(loadOmniHubScripts, 3000);

  // アカウント関連要素にインタラクションがあった時
  document.addEventListener("click", (e) => {
    if (
      e.target.closest('[href*="account"]') ||
      e.target.closest("[data-customer-id]") ||
      e.target.closest(".header__icon-wrapper")
    ) {
      loadOmniHubScripts();
    }
  });
}

/**
 * 汎用 コピー&ペースト
 */
function initializeCopyToClipboard() {
  const DEBUG_COPY = false;
  const COPIED_TIMEOUT = 2000;

  const toggleIcons = (wrapper, isCopied) => {
    const copyIcon = wrapper.querySelector('.c_c-copy-wrapper__icon-copy');
    const checkIcon = wrapper.querySelector('.c_c-copy-wrapper__icon-check');

    if (!copyIcon || !checkIcon) return;

    if (isCopied) {
      copyIcon.classList.remove('is-active');
      checkIcon.classList.add('is-active');
    } else {
      copyIcon.classList.add('is-active');
      checkIcon.classList.remove('is-active');
    }
  };

  const fallbackCopy = (value) => {
    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.style.cssText = 'position:fixed;left:-999999px;';
    document.body.appendChild(textarea);
    textarea.select();

    let success = false;
    try {
      success = document.execCommand('copy');
    } catch (error) {
      if (DEBUG_COPY) console.error('Fallback copy failed:', error);
    } finally {
      document.body.removeChild(textarea);
    }

    return success;
  };

  const copyText = async (value) => {
    if (!value) return false;

    if (!navigator.clipboard?.writeText) {
      return fallbackCopy(value);
    }

    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch (error) {
      if (DEBUG_COPY) console.warn('navigator.clipboard failed, using fallback.', error);
      return fallbackCopy(value);
    }
  };

  const attachCopyHandler = (wrapper) => {
    if (wrapper.dataset.copyInitialized === 'true') return;

    wrapper.addEventListener('click', async () => {
      const value = wrapper.dataset.copyValue?.trim();
      const copied = await copyText(value);

      if (!copied) return;

      toggleIcons(wrapper, true);
      setTimeout(() => toggleIcons(wrapper, false), COPIED_TIMEOUT);
    });

    wrapper.dataset.copyInitialized = 'true';
  };

  const existingWrappers = document.querySelectorAll('.c_c-copy-wrapper');

  if (!existingWrappers.length) return;

  existingWrappers.forEach(attachCopyHandler);
}

/**
 * カート内 削除リンク（「お気に入りに登録して削除」「削除する」）の AJAX 化
 *
 * 既存挙動: <a href="/cart/change?id=...&quantity=0"> をクリックして GET 遷移。
 * Shopify 公式が POST /cart/change.js を推奨しており、line item key 形式 (variant_id:hash)
 * の GET が稀に反応しない事象があったため AJAX 化する。
 */
function initializeCartLineItemRemove() {
  const cartListEl = document.querySelector('.line-item-table__list');
  if (!cartListEl) return;

  // capture phase で intercept することで Focal の <line-item-quantity> delegate (bubble phase) より先に処理
  cartListEl.addEventListener(
    'click',
    async (e) => {
      const link = e.target.closest('a.line-item__remove-button');
      if (!link) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      // 既に処理中なら無視（class を source of truth とする）
      if (cartListEl.classList.contains('is-cart-removing')) return;

      const lineItem = link.closest('.c_c-cart-line-item');
      if (lineItem) lineItem.classList.add('is-processing');
      cartListEl.classList.add('is-cart-removing');

      try {
        if (link.classList.contains('c_js-cart-favorite-remove-link')) {
          const { handle, variantId, customerId } = link.dataset;
          if (customerId && handle && variantId) {
            // 待たない。応答が遅く、待つとクリックが無反応に見えるため
            const favorite = window
              .submitFavorite('/tools/prime-review/api/addFavorite', handle, Number(variantId))
              .catch((err) => console.warn('[cart-remove] お気に入りの登録に失敗しました', err));

            if (!window.supportsFetchKeepalive) await favorite;
          }
        }

        const lineItemKey = new URLSearchParams(link.search).get('id');
        if (!lineItemKey) {
          window.location.href = link.href;
          return;
        }

        const cartChangeUrl = window.themeVariables?.routes?.cartChangeUrl || '/cart/change';
        const response = await fetch(`${cartChangeUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ id: lineItemKey, quantity: 0 }),
        });

        if (!response.ok) throw new Error(`cart change responded ${response.status}`);

        window.location.reload();
      } catch (err) {
        console.warn('[cart-remove] AJAX 削除に失敗、GET 遷移にフォールバック', err);
        // フォールバック navigation 前に視覚状態を解除（reload 時はクラスを保持して dim のまま遷移）
        if (lineItem) lineItem.classList.remove('is-processing');
        cartListEl.classList.remove('is-cart-removing');
        window.location.href = link.href;
      }
    },
    { capture: true }
  );
}

/**
 * カート内 ギフトボックスのカート追加 AJAX 化
 *
 * 既存挙動: クリック時に class からハードコードされた variant ID を引いて
 * /cart/add.js を呼び、成功後 document.location.href = '/cart' で全ページリロード。
 * → Cart AJAX API + Section Rendering API による partial section refresh に置き換え。
 *
 * sections パラメータには JSON テンプレートの section instance ID（template prefix 付き、
 * 例: template--17210308460599__main）を渡すことで block 構成（gift_info 等）を保ったまま
 * レスポンスに section HTML を含められる。sections_url で cart テンプレートの context を指定する。
 */
function initializeGiftOptionAddToCart() {
  document.addEventListener('click', async (e) => {
    const item = e.target.closest('.gift-item[data-variant-id]');
    if (!item) return;
    // 処理中（クリック中のアイテムに is-processing が残っている間）は無視
    if (document.querySelector('.gift-item.is-processing')) return;

    const variantId = Number(item.dataset.variantId);
    if (!variantId) return;

    const sectionWrapper = item.closest('[id^="shopify-section-"]');
    const sectionId = sectionWrapper?.id.replace('shopify-section-', '');
    item.classList.add('is-processing');

    try {
      const routes = window.themeVariables?.routes || {};
      const cartAddUrl = routes.cartAddUrl || '/cart/add';
      const cartUrl = routes.cartUrl || '/cart';

      const addResponse = await fetch(`${cartAddUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: sectionId ? [sectionId, 'mini-cart'] : ['mini-cart'],
          sections_url: cartUrl,
        }),
      });

      if (!addResponse.ok) {
        const errData = await addResponse.json().catch(() => ({}));
        throw new Error(errData.description || `cart add responded ${addResponse.status}`);
      }

      const addData = await addResponse.json();

      // main-cart セクションを返却 HTML で差し替え
      if (sectionWrapper && sectionId && addData.sections?.[sectionId]) {
        sectionWrapper.innerHTML = addData.sections[sectionId];
      }

      // ミニカート/送料バー/ヘッダーバッジ同期用に最新カート object を取得
      const cart = await (await fetch(`${cartUrl}.js`)).json();
      cart.sections = addData.sections;
      const detail = { cart };
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:updated', { bubbles: true, detail })
      );
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { ...detail, replacementDelay: 0 },
        })
      );
    } catch (err) {
      console.warn('[gift-option] AJAX 追加に失敗、リロードにフォールバック', err);
      window.location.reload();
    } finally {
      // section 差し替え後は item が detached になるため class 解除は no-op だが
      // section 差し替えが起きないエッジケース（fetch 失敗等）で必要
      item.classList.remove('is-processing');
    }
  });
}

// Focal も theme.js で [data-smooth-scroll] を見ている。属性名を分けないと
// closest() が拾う要素が食い違い、どちらか一方が必ず誤作動する
function initializeSmoothScroll() {
  // オフセットはターゲット側の scroll-margin が持つ（Focal の .anchor）。
  // ここでインラインスタイルを書くと打ち消してしまう
  function scrollToTarget(element) {
    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    const behavior = prefersReduced ? "auto" : "smooth";

    element.scrollIntoView({ behavior });
  }

  // スクロールだけではスクリーンリーダーの読み上げ位置が移らない
  function focusTarget(element) {
    element.focus({ preventScroll: true });
    if (document.activeElement !== element) {
      element.setAttribute("tabindex", "-1");
      element.focus({ preventScroll: true });
    }
  }

  function handleClick(event) {
    if (event.button !== 0) return;
    // 新しいタブ・ウィンドウで開く操作を横取りしない
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target.closest("a[data-anchor-scroll]");
    if (!link) return;

    const hash = link.hash;
    if (!hash) return;
    // link.hash は遷移先に関係なく返る。同名の id が今のページにあると乗っ取ってしまう
    if (link.origin !== location.origin) return;
    if (link.pathname !== location.pathname) return;

    const target =
      document.getElementById(decodeURIComponent(hash.slice(1))) ||
      (hash === "#top" && document.body);

    if (!target) return;

    event.preventDefault();
    scrollToTarget(target);
    focusTarget(target);

    // 用途がページ内の索引ナビしかないため、タップのたびに履歴を積まない。
    // 戻るボタンは「このページから出る」の意味を保つ
    history.replaceState({}, "", hash);
  }

  document.addEventListener("click", handleClick, { capture: true });
}

const MODAL_CONTENT_SELECTOR = 'img, video, button, a, input, select, textarea, label, [data-modal-content]';
// ドラッグ終了時にも click が飛ぶため、この移動量までを「クリック」とみなす
const MODAL_CLICK_SLOP = 10;

/**
 * Modal Manager
 * data属性でタイプを指定し、分岐処理を自動化
 */
class ModalManager {
  constructor() {
    // 閉じずに DOM から外れても参照が残らないよう WeakMap にする
    this.modals = new WeakMap();
    this.currentOpenTrigger = null;
    this.callbacks = new WeakMap();
    this.splideInstances = {}; // 外部から登録されるSplideインスタンス
    this.splideFactories = {}; // 初回open時にmountするためのSplideファクトリ
    this.delegateTriggers();
    this.init();
  }

  /**
   * 初期化：data-modal-type を持つ dialog を管理対象に追加
   */
  init() {
    const targets = document.querySelectorAll('dialog[data-modal-type]');

    if (targets.length === 0) return;

    targets.forEach((modal) => {
      this.registerModal(modal);
    });
  }

  /**
   * Splideインスタンスを登録（セクション側から呼び出し）
   * @param {string} modalId - モーダルID
   * @param {Object} splideInstance - Splideインスタンス
   */
  registerSplideInstance(modalId, splideInstance) {
    this.splideInstances[modalId] = splideInstance;

    // 既に登録されているモーダルがあれば、そのコールバックを更新
    document.querySelectorAll('dialog[data-splide-id]').forEach((modal) => {
      if (modal.dataset.splideId !== modalId) return;
      if (modal.dataset.modalType !== 'splide') return;
      if (!this.modals.has(modal)) return;

      this.callbacks.set(modal, this.getCallbacksByType('splide', modal, {}));
    });
  }

  /**
   * Splideファクトリを登録（初回open時にmountする lazy 用途）
   * @param {string} modalId - モーダルID（data-splide-id 属性値）
   * @param {Function} factoryFn - mount済みSplideインスタンスを返す関数
   */
  registerSplideFactory(modalId, factoryFn) {
    this.splideFactories[modalId] = factoryFn;
  }

  /**
   * 登録済みインスタンスを外す。破棄されたものが残ると、次に開いたときに
   * ファクトリが呼ばれず死んだ Splide を操作してしまう
   */
  unregisterSplideInstance(modalId) {
    delete this.splideInstances[modalId];
  }

  /** ファクトリは要素を掴んだままになるため、要素が外れたら捨てる */
  unregisterSplideFactory(modalId) {
    delete this.splideFactories[modalId];
  }

  /**
   * モーダルを管理対象に登録
   * @param {HTMLElement} modal - モーダル要素
   * @param {Object} options - オプション設定（省略可）
   */
  registerModal(modal, options = {}) {
    if (!modal.id) {
      console.error('Modal element must have an id attribute');
      return;
    }

    // 各モーダルの状態を初期化
    this.modals.set(modal, {
      isTransitioning: false,
      isOpen: false,
      listeners: null,
      splideMoveListener: null,
    });

    // data属性からタイプを取得
    const modalType = modal.getAttribute('data-modal-type') || 'simple';

    // タイプに応じたコールバックを自動設定
    const autoCallbacks = this.getCallbacksByType(modalType, modal, options);

    // 手動指定のオプションと自動生成のコールバックをマージ
    const mergedCallbacks = { ...autoCallbacks, ...options };

    if (Object.keys(mergedCallbacks).length > 0) {
      this.callbacks.set(modal, mergedCallbacks);
    }

    this.setupTriggers(modal);
  }

  /**
   * タイプに応じたコールバックを生成
   * @param {string} type - モーダルタイプ
   * @param {HTMLElement} _modal - モーダル要素（将来拡張用、現在未使用）
   * @param {Object} _options - ユーザー指定のオプション（将来拡張用、現在未使用）
   */
  getCallbacksByType(type, _modal, _options) {
    const callbacks = {};

    switch (type) {
      case 'splide':
        callbacks.onTriggerClick = (trigger, modalEl) => {
          const currentIndex = parseInt(
            trigger.getAttribute('data-modal-dialog'),
            10
          ) - 1;

          const splideId = modalEl.getAttribute('data-splide-id');
          let splide = this.splideInstances[splideId];

          // ファクトリは mount を要求するだけ。登録は CSplideDialog.afterMount が行う
          if (!splide && this.splideFactories[splideId]) {
            try {
              this.splideFactories[splideId]();
              splide = this.splideInstances[splideId];
            } catch (e) {
              console.error(`Splide factory for ${splideId} failed:`, e);
            }
          }

          if (splide) {
            splide.go(currentIndex);

            const dialogContainer = modalEl.querySelector('.dialog-container');
            if (dialogContainer) {
              dialogContainer.classList.add('splide--loaded');
            }

            const updateGalleryIndex = () => {
              const galleryIndexElements = modalEl.querySelectorAll('[class*="--current"]');
              galleryIndexElements.forEach((el) => {
                el.textContent = splide.index + 1;
              });
            };

            updateGalleryIndex();

            // updateGalleryIndex は modalEl を掴む。状態レコードへ置いて要素と一緒に回収させる
            const state = this.modals.get(modalEl);
            if (splide.on && state) {
              splide.on('move', updateGalleryIndex);
              state.splideMoveListener = updateGalleryIndex;
            }
          }
        };

        callbacks.onOpen = (modalEl) => {
          const splideId = modalEl.getAttribute('data-splide-id');
          const splide = this.splideInstances[splideId];
          if (splide) {
            // mount はモーダルを開く前に走り幅 0 で組まれるため、ここで実寸に組み直す
            splide.refresh();
          }
        };

        // Splideモーダルのクローズ時にリスナーを削除
        callbacks.onClose = (modalEl) => {
          const state = this.modals.get(modalEl);
          if (!state?.splideMoveListener) return;

          const splide = this.splideInstances[modalEl.getAttribute('data-splide-id')];
          splide?.off?.('move', state.splideMoveListener);
          state.splideMoveListener = null;
        };
        break;

      case 'simple':
        // シンプルなモーダルはデフォルト処理のみ
        break;

      case 'custom':
        // カスタムコールバックはオプションで指定
        break;

      default:
        console.warn(`Unknown modal type: ${type}`);
    }

    return callbacks;
  }

  /**
   * 開くトリガーを document で受けて委譲する。
   * Splide の type: 'loop' が作るクローンは mount 時に増え、mount の順番は
   * data-splide-move-to による移動で変わるため、要素へ直接張ると取りこぼす
   */
  delegateTriggers() {
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest?.('[data-modal-open]');
      if (!trigger) return;

      const modal = document.getElementById(trigger.getAttribute('data-modal-open'));
      if (!modal || !this.modals.has(modal)) return;

      event.preventDefault();
      this.currentOpenTrigger = trigger;

      const callbacks = this.callbacks.get(modal);
      if (callbacks?.onTriggerClick) {
        callbacks.onTriggerClick(trigger, modal);
      }

      this.openModal(modal);
    });

    ['mousedown', 'keydown'].forEach((type) => {
      document.addEventListener(type, (event) => {
        if (!event.target.closest?.('[data-modal-open]')) return;
        ModalManager.handleTriggerFocus(event);
      });
    });
  }

  /**
   * 閉じるトリガーのセットアップ（開くトリガーは delegateTriggers が受ける）
   */
  setupTriggers(modal) {
    const openTriggers = document.querySelectorAll(
      `[data-modal-open="${modal.id}"]`
    );
    const closeTriggers = modal.querySelectorAll('[data-modal-close]');

    if (openTriggers.length === 0 && closeTriggers.length === 0) {
      console.warn(`No triggers found for modal: ${modal.id}`);
      return;
    }

    // 閉じるトリガー
    closeTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.closeModal(modal);
      });
    });
  }

  /**
   * モーダルを開く
   */
  openModal(modal) {
    const state = this.modals.get(modal);
    if (!state || state.isTransitioning || state.isOpen) return;

    state.isTransitioning = true;

    modal.showModal();
    this.backfaceFixed(true);
    this.attachModalListeners(modal);

    requestAnimationFrame(async () => {
      modal.setAttribute('data-active', 'true');
      await this.waitModalAnimation(modal);

      // オープンコールバック実行
      const callbacks = this.callbacks.get(modal);
      if (callbacks?.onOpen) {
        callbacks.onOpen(modal);
      }

      state.isTransitioning = false;
      state.isOpen = true;
    });
  }

  /**
   * モーダルを閉じる
   */
  async closeModal(modal) {
    const state = this.modals.get(modal);
    if (!state || state.isTransitioning || !state.isOpen) return;

    state.isTransitioning = true;

    modal.setAttribute('data-active', 'false');
    this.backfaceFixed(false);
    this.detachModalListeners(modal);

    await this.waitModalAnimation(modal);
    modal.close();

    // クローズコールバック実行
    const callbacks = this.callbacks.get(modal);
    if (callbacks?.onClose) {
      callbacks.onClose(modal);
    }

    if (this.currentOpenTrigger) {
      this.currentOpenTrigger.focus();
      this.currentOpenTrigger = null;
    }

    state.isTransitioning = false;
    state.isOpen = false;
  }

  /**
   * モーダルのアニメーション終了を待つ
   */
  waitModalAnimation(modal) {
    if (modal.getAnimations().length === 0) {
      return Promise.resolve([]);
    }
    return Promise.allSettled(
      Array.from(modal.getAnimations()).map((animation) => animation.finished)
    );
  }

  attachModalListeners(modal) {
    const state = this.modals.get(modal);
    if (!state) return;

    const controller = new AbortController();
    const { signal } = controller;
    let pointerStart = null;

    modal.addEventListener(
      'pointerdown',
      (event) => {
        pointerStart = { x: event.clientX, y: event.clientY };
      },
      { signal }
    );

    modal.addEventListener(
      'click',
      (event) => {
        // どの経路で抜けても古い座標が残らないよう、判定より先に読み出して消す
        const start = pointerStart;
        pointerStart = null;

        if (event.target.closest(MODAL_CONTENT_SELECTOR)) return;

        // pointerdown を伴わない click は素性が読めないため閉じない
        if (!start) return;

        const moved = Math.abs(event.clientX - start.x) + Math.abs(event.clientY - start.y);
        if (moved > MODAL_CLICK_SLOP) return;

        this.closeModal(modal);
      },
      { signal }
    );

    window.addEventListener(
      'keydown',
      (event) => {
        document.documentElement.removeAttribute('data-mousedown');
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeModal(modal);
        }
      },
      { signal }
    );

    state.listeners = controller;
  }

  detachModalListeners(modal) {
    const state = this.modals.get(modal);
    if (!state) return;

    state.listeners?.abort();
    state.listeners = null;
  }

  /**
   * フォーカス時のマウスダウン/キーダウンを判定
   */
  static handleTriggerFocus(event) {
    if (event.type === 'mousedown') {
      document.documentElement.setAttribute('data-mousedown', 'true');
    }
    if (event.type === 'keydown') {
      document.documentElement.removeAttribute('data-mousedown');
    }
  }

  /**
   * スクロール時の背景固定処理
   */
  backfaceFixed(fixed) {
    const scrollBarWidth = this.getScrollBarSize();
    const scrollPosition = this.getScrollPosition(fixed);

    document.body.style.paddingInlineEnd = fixed ? scrollBarWidth + 'px' : '';
    this.applyStyles(scrollPosition, fixed);

    if (!fixed) {
      this.restorePosition(scrollPosition);
    }
  }

  /**
   * スクロールバーのサイズを取得
   */
  getScrollBarSize() {
    const scrollBarXSize = window.innerHeight - document.body.clientHeight;
    const scrollBarYSize = window.innerWidth - document.body.clientWidth;
    return this.isVerticalWritingMode() ? scrollBarXSize : scrollBarYSize;
  }

  /**
   * スクロール位置を取得
   */
  getScrollPosition(fixed) {
    if (fixed) {
      return this.isVerticalWritingMode()
        ? document.scrollingElement.scrollLeft || 0
        : document.scrollingElement.scrollTop || 0;
    }
    return parseInt(document.body.style.insetBlockStart || '0', 10);
  }

  /**
   * スタイルを適用
   */
  applyStyles(scrollPosition, apply) {
    const isVertical = this.isVerticalWritingMode();
    const styles = {
      blockSize: '100dvb',
      insetInlineStart: '0',
      position: 'fixed',
      insetBlockStart: isVertical
        ? scrollPosition + 'px'
        : scrollPosition * -1 + 'px',
      inlineSize: '100dvi',
    };

    Object.keys(styles).forEach((key) => {
      document.body.style[key] = apply ? styles[key] : '';
    });
  }

  /**
   * スクロール位置を復元
   */
  restorePosition(scrollPosition) {
    const options = {
      behavior: 'instant',
      [this.isVerticalWritingMode() ? 'left' : 'top']: this.isVerticalWritingMode()
        ? scrollPosition
        : scrollPosition * -1,
    };
    window.scrollTo(options);
  }

  /**
   * 縦書きモード判定
   */
  isVerticalWritingMode() {
    const writingMode = window.getComputedStyle(document.documentElement)
      .writingMode;
    return writingMode.includes('vertical');
  }
}

// DOMContentLoaded で生成すると、パース中に登録された他のハンドラのほうが先に走って掴めない。
// c_common.js は defer なので、トップレベルでも .modal の走査に必要な DOM は揃っている
window.modalManager = new ModalManager();

/**
 * <c-privacy-bar> 顧客プライバシーバー。
 *
 * 表示可否は Customer Privacy API の shouldShowBanner() に委ねる。対象地域と同意済みかどうかは
 * 管理画面（お客様のプライバシー）が持つため、テーマ側では判定しない。
 *
 * 同意内容の保存先は Shopify が持つ。テーマ側は localStorage にも Cookie にも書かない。
 * sale_of_data は渡さない。米国向けは管理画面のオプトアウトページが担う。
 */
class CPrivacyBar extends HTMLElement {
  connectedCallback() {
    if (!window.Shopify?.loadFeatures) return;

    this.dialog = document.getElementById('privacy-preferences');

    // class="modal" を付けていないため ModalManager.init() の走査に乗らない。ここで登録する
    if (this.dialog && !window.modalManager.modals.has(this.dialog)) {
      window.modalManager.registerModal(this.dialog);
    }

    this.controller = new AbortController();
    const { signal } = this.controller;
    const onClick = this.handleClick.bind(this);

    this.addEventListener('click', onClick, { signal });
    this.dialog?.addEventListener('click', onClick, { signal });

    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      (error) => {
        if (error) return;
        this.setup();
      }
    );

    // テーマエディターでは shouldShowBanner() が false でも中身を確認できないため、
    // フッターセクションを選択している間だけ出す
    if (window.Shopify.designMode) {
      document.addEventListener(
        'shopify:section:select',
        (event) => {
          if (event.target.contains(this)) this.hidden = false;
        },
        { signal }
      );

      document.addEventListener(
        'shopify:section:deselect',
        (event) => {
          if (event.target.contains(this)) this.hidden = true;
        },
        { signal }
      );
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  get privacy() {
    return window.Shopify.customerPrivacy;
  }

  setup() {
    this.syncCheckboxes();
    document
      .querySelectorAll('[data-privacy-preferences-entry]')
      .forEach((entry) => {
        entry.hidden = false;
      });

    if (this.privacy.shouldShowBanner()) {
      this.hidden = false;
    }
  }

  handleClick(event) {
    const action = event.target.closest('[data-privacy-action]')?.dataset
      .privacyAction;
    if (!action) return;

    if (action === 'accept') {
      this.save({ analytics: true, marketing: true, preferences: true });
      return;
    }

    if (action === 'decline') {
      this.save({ analytics: false, marketing: false, preferences: false });
      return;
    }

    if (action === 'save') {
      this.save(this.checkedConsent());
    }
  }

  checkedConsent() {
    const consent = {};

    this.dialog?.querySelectorAll('[data-privacy-consent]').forEach((input) => {
      consent[input.dataset.privacyConsent] = input.checked;
    });

    return consent;
  }

  syncCheckboxes() {
    const current = this.privacy.currentVisitorConsent();

    this.dialog?.querySelectorAll('[data-privacy-consent]').forEach((input) => {
      // 返り値は 'yes' / 'no' / '' の文字列。将来 boolean になっても壊れないよう両方見る
      const value = current[input.dataset.privacyConsent];
      input.checked = value === true || value === 'yes';
    });
  }

  save(consent) {
    this.privacy.setTrackingConsent(consent, () => {
      this.hidden = true;
      this.syncCheckboxes();

      if (this.dialog?.open) {
        window.modalManager.closeModal(this.dialog);
      }
    });
  }
}

customElements.define('c-privacy-bar', CPrivacyBar);

class FavoriteProducts extends HTMLElement {
  async connectedCallback() {
    if (this.searchQueryString === '') {
      this.innerHTML = '';
      return;
    }

    try {
      const response = await fetch(
        `${window.themeVariables.routes.searchUrl}?type=product&q=${this.searchQueryString}&section_id=${this.sectionId}`
      );
      if (!response.ok) throw new Error('HTTP ' + response.status);
      const div = document.createElement('div');
      div.innerHTML = await response.text();
      const favoriteProductsElement = div.querySelector('favorite-products');
      if (favoriteProductsElement && favoriteProductsElement.hasChildNodes()) {
        this.innerHTML = favoriteProductsElement.innerHTML;
      } else {
        // 結果0件: 初期表示のスケルトンを除去
        this.innerHTML = '';
      }
    } catch (error) {
      // 取得失敗: スケルトンを除去（無限ローディング防止）
      console.error('favorite-products fetch failed:', error);
      this.innerHTML = '';
    }
  }

  get searchQueryString() {
    const raw = this.getAttribute('product-ids');
    if (!raw) return '';

    try {
      const items = JSON.parse(raw);
      if (!Array.isArray(items) || items.length === 0) return '';
      return items
        .slice()
        .reverse()
        .map((id) => 'id:' + id)
        .join(' OR ');
    } catch {
      return '';
    }
  }

  get sectionId() {
    return this.getAttribute('section-id');
  }

  get productsCount() {
    return parseInt(this.getAttribute('products-count'), 10) || 6;
  }
}
window.customElements.define('favorite-products', FavoriteProducts);

/** Splide の共通基底クラス。汎用の <c-splide> で足りるなら継承は不要 */
class CSplideElement extends HTMLElement {
  connectedCallback() {
    // 移動すると disconnected / connected が同期で走る。再入時は移動済みなので先へ進む
    if (this.moveToTarget()) return;

    if (this.dataset.splideDefer !== undefined) {
      this.observeViewport();
      return;
    }
    this.requestMount();
  }

  requestMount() {
    CSplideElement.ready()
      ?.then(() => {
        // 待っている間にエディタが要素を差し替えることがある
        if (!this.isConnected || this.splide) return;
        this.mountSplide();
      })
      .catch((error) => console.error('c-splide: Splide を読み込めなかった', error));
  }

  /** data-splide-defer: 画面に近づくまで mount しない。data-splide-lazy は Splide が画像用に使う名前なので避ける */
  observeViewport() {
    if (!window.IntersectionObserver) {
      this.requestMount();
      return;
    }

    // 400px は見え始める前に mount を終わらせるための先読み幅
    this.lazyObserver = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        this.lazyObserver.disconnect();
        this.requestMount();
      },
      { rootMargin: '400px 0px' }
    );
    this.lazyObserver.observe(this);
  }

  /** ready() を待たずその場で mount する。ModalManager がモーダルを開くときに呼ぶ */
  mountNow() {
    if (this.splide || !window.Splide) return;

    this.mountSplide();
  }

  /**
   * 記事本文に置かれた空の div（data-splide-move-to の id）へ自分を移す。移したら true を返す。
   * mount 前に呼ぶこと。mount 済みで移すと disconnectedCallback が destroy してしまう
   */
  moveToTarget() {
    const targetId = this.dataset.splideMoveTo;
    if (!targetId) return false;

    const target = document.getElementById(targetId);
    if (!target || target === this.parentElement) return false;

    target.appendChild(this);
    return true;
  }

  disconnectedCallback() {
    this.lazyObserver?.disconnect();

    // mount 前に外れることがあるため、継承先の後始末は mount 済みのときだけ呼ぶ
    if (!this.splide) return;
    this.beforeDestroy();
    this.teardownVideos();
    this.splide.destroy(true);
    this.splide = null;
  }

  mountSplide() {
    // Splide 内蔵の reducedMotion は autoplay を 'pause' にするだけで送りは止まらない
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const options = { ...this.splideOptions };
    const autoplay = options.autoplay && !reduceMotion;
    options.autoplay = autoplay;
    // セクション側の指定を優先しつつ、未指定のキーだけ共通の翻訳で埋める
    options.i18n = { ...CSplideElement.i18n, ...options.i18n };

    this.splide = new window.Splide(this, options);
    // 同梱の Splide 4.1.2 はドラッグ中にクラスを付けない
    this.splide.on('drag', () => this.classList.add('is-dragging'));
    this.splide.on('dragged', () => this.classList.remove('is-dragging'));
    // 自前でホバーを見るとフォーカスやドラッグでの停止を取りこぼし、進捗バーだけ進んでしまう
    this.splide.on('autoplay:pause', () => this.style.setProperty('--_progress-state', 'paused'));
    this.splide.on('autoplay:play', () => this.style.removeProperty('--_progress-state'));
    this.beforeMount();
    this.splide.mount();
    // mount 時点はレイアウトが確定しておらず、Splide が余白を含めずに位置を計算して
    // 1 枚目が左端へ寄る。描画前に測り直す。refresh() はクローンまで作り直すので使わない
    this.splide.Components.Layout.resize(true);

    // Splide のイベントは登録順に発火する。mount 前に張ると splide.index とスライド DOM が確定していない
    this.setupVideos();
    this.splide.on('refresh', () => this.setupVideos());
    if (this.splide.options.type === 'fade') {
      // move は遷移の開始時に飛ぶので、フェード中に遷移先の読み込みを始められる
      this.splide.on('move', (index) => this.showActiveVideo(index));
    }

    // Focal のセクションアニメーションは play-state の切り替えでしか作り直せない
    if (autoplay && this.dataset.splideProgressRestart !== undefined) {
      this.splide.on('move', () => this.restartSectionAnimation());
      this.restartSectionAnimation();
    }

    this.afterMount();
  }

  /** 全枚数を常時再生すると通信量が枚数分積み上がる */
  setupVideos() {
    this.teardownVideos();

    this.videos = Array.from(this.querySelectorAll('.splide__slide video'));
    if (!this.videos.length) return;

    // 画面外のセクションで読み込みが走らないよう、observer の初回配信を待つ
    this.inViewport = !window.IntersectionObserver;
    if (window.IntersectionObserver) {
      this.viewportObserver = new IntersectionObserver((entries) => {
        this.inViewport = entries[0].isIntersecting;
        this.applyVideos();
      });
      this.viewportObserver.observe(this);
    }

    // fade は全スライドが同じ位置に重なるため、トラック内の位置では判定できない
    if (this.splide.options.type === 'fade') {
      this.showActiveVideo(this.splide.index);
      return;
    }
    if (!window.IntersectionObserver) return;

    // is-visible はトラックへ完全に収まったスライドにしか付かず、見切れたカードを取りこぼす
    const slideWidth = Math.round(this.querySelector('.splide__slide').getBoundingClientRect().width) || 400;
    this.trackObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.playable.add(entry.target);
          } else {
            this.playable.delete(entry.target);
          }
        });
        this.applyVideos();
      },
      // rootMargin の % はトラック幅基準になり前後 3〜4 枚に広がるため、スライド 1 枚分の px で指定する
      { root: this.querySelector('.splide__track'), rootMargin: `0px ${slideWidth}px`, threshold: 0 }
    );
    this.videos.forEach((video) => this.trackObserver.observe(video));
  }

  showActiveVideo(index) {
    if (!this.videos.length) return;

    const active = this.splide.Components.Slides.getAt(index);
    this.playable = new WeakSet();
    this.videos.forEach((video) => {
      if (active && video.closest('.splide__slide') === active.slide) this.playable.add(video);
    });
    this.applyVideos();
  }

  applyVideos() {
    if (!this.videos) return;

    this.videos.forEach((video) => {
      // 端末ごとに出し分けた非表示の動画まで読むと、モバイルでデスクトップ用を落としてしまう
      const play = this.inViewport && this.playable.has(video) && video.offsetParent !== null;

      video.preload = play ? 'auto' : 'none';
      if (!play) {
        video.pause();
        return;
      }
      if (!video.paused) return;

      video.muted = true;
      video.play().catch(() => {});
    });
  }

  teardownVideos() {
    this.viewportObserver?.disconnect();
    this.trackObserver?.disconnect();
    this.viewportObserver = null;
    this.trackObserver = null;
    this.videos = [];
    this.playable = new WeakSet();
  }

  restartSectionAnimation() {
    const section = this.closest('.shopify-section');
    if (!section) return;

    section.style.setProperty('--section-animation-play-state', 'paused');
    requestAnimationFrame(() => section.style.setProperty('--section-animation-play-state', 'running'));
  }

  /** 継承先で上書きする */
  get splideOptions() {
    return {};
  }

  beforeMount() {}

  afterMount() {}

  beforeDestroy() {}

  /**
   * splide.min.js の読み込み完了。読み込まないテンプレートでは null を返す。
   * エディタが後から差し込んだセクションのため、呼ばれるたびに script を探し直す。
   */
  static ready() {
    if (window.cSplideReady) return window.cSplideReady;
    if (window.Splide) {
      window.cSplideReady = Promise.resolve();
      return window.cSplideReady;
    }

    const script = document.querySelector('script[src*="splide.min.js"]');
    if (!script) return null;

    window.cSplideReady = new Promise((resolve, reject) => {
      script.addEventListener('load', () => resolve(), { once: true });
      script.addEventListener('error', () => reject(new Error('splide.min.js の読み込みに失敗')), { once: true });
    });
    return window.cSplideReady;
  }

  /** Splide の既定は英語。翻訳は c_library-loader が JSON で出す */
  static get i18n() {
    if (CSplideElement.i18nCache) return CSplideElement.i18nCache;

    const source = document.getElementById('c-splide-i18n');
    try {
      CSplideElement.i18nCache = source ? JSON.parse(source.textContent) : {};
    } catch (error) {
      console.error('c-splide: 支援技術向けの文言を解釈できなかった', error);
      CSplideElement.i18nCache = {};
    }
    return CSplideElement.i18nCache;
  }
}

// 発火済みの error は後から検知できず永久に pending になるため、リスナはこの時点で張る
CSplideElement.ready();

window.CSplideElement = CSplideElement;

/** <c-splide class="splide" data-splide-options='{"type":"loop","perPage":2}'> */
class CSplide extends CSplideElement {
  get splideOptions() {
    try {
      return JSON.parse(this.dataset.splideOptions || '{}');
    } catch (error) {
      console.error('c-splide: data-splide-options を解釈できなかった', error);
      return {};
    }
  }
}
window.customElements.define('c-splide', CSplide);

/**
 * <c-splide-dialog id="splide-1--dialog" class="splide" data-splide-mount="on-modal-open">
 * 開いたときのスライド位置は ModalManager が data-modal-dialog から決めるため、ここでは触らない
 */
class CSplideDialog extends CSplide {
  /**
   * data-splide-mount="on-modal-open": 開かれないまま終わる dialog の mount を省く。
   * connectedCallback ごと差し替えると、基底の moveToTarget() と data-splide-defer が効かなくなる
   */
  requestMount() {
    if (this.dataset.splideMount !== 'on-modal-open') {
      super.requestMount();
      return;
    }
    window.modalManager.registerSplideFactory(this.id, () => this.mountNow());
  }

  disconnectedCallback() {
    // beforeDestroy は mount 済みのときしか呼ばれないため、ファクトリはここで外す
    window.modalManager.unregisterSplideFactory(this.id);
    super.disconnectedCallback();
  }

  /** dialog は 1 枚ずつ中央に出す。data-splide-options 側の指定を優先する */
  get splideOptions() {
    return {
      type: 'loop',
      speed: 1000,
      gap: 20,
      focus: 'center',
      trimSpace: false,
      pagination: false,
      keyboard: true,
      ...super.splideOptions,
    };
  }

  beforeDestroy() {
    window.modalManager.unregisterSplideInstance(this.id);
  }

  beforeMount() {
    // overflow は mount 中に発火するため、リスナは mount 前に張る。
    // トラックに収まりきるときに clones を残すと、1 枚しかないスライドが複製される
    this.splide.on('overflow', (isOverflow) => {
      this.splide.options = { clones: isOverflow ? undefined : 0 };
    });
  }

  afterMount() {
    window.modalManager.registerSplideInstance(this.id, this.splide);
  }
}
window.customElements.define('c-splide-dialog', CSplideDialog);

document.addEventListener("DOMContentLoaded", () => {
  // Appify関連の初期化
  isAppifyHidden();
  // スケルトンローダーの初期化
  initSkeletonLoader();
  // カードイベントの初期化
  cardEvents.initializeCards();
  // アコーディオンの初期化
  initializeAccordion();
  // お気に入り機能の初期化
  initializeFavorites();
  // 遅延読み込みスクリプトの初期化
  initializeLazyLoadedScripts();
  // コピー&ペースト機能の初期化
  initializeCopyToClipboard();
  // カート削除リンク（お気に入り追加併用含む）の AJAX 化
  initializeCartLineItemRemove();
  // カート ギフトボックス追加の AJAX 化
  initializeGiftOptionAddToCart();
  // スムーススクロールの初期化
  initializeSmoothScroll();
});

/**
 * 横スクロールの前後のボタン（c_scroller-buttons）。
 * 子の [data-scroller-list] を見える幅の分だけ送り、先頭・末尾ではボタンを無効にする。
 */
if (!customElements.get('c-scroller')) {
  class CScroller extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-scroller-list]');
      this.prev = this.querySelector('[data-scroller-prev]');
      this.next = this.querySelector('[data-scroller-next]');
      if (!this.list) return;

      this.controller = new AbortController();
      const { signal } = this.controller;
      this.update = this.update.bind(this);

      this.prev?.addEventListener('click', () => this.slide(-1), { signal });
      this.next?.addEventListener('click', () => this.slide(1), { signal });
      this.list.addEventListener('scroll', this.update, { passive: true, signal });
      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this.list);
      this.mutationObserver = new MutationObserver(this.update);
      this.mutationObserver.observe(this.list, { subtree: true, attributeFilter: ['hidden'] });
      this.update();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.mutationObserver?.disconnect();
    }

    slide(direction) {
      const paddingStart = parseFloat(getComputedStyle(this.list).paddingInlineStart) || 0;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.list.scrollBy({
        left: direction * (this.list.clientWidth - paddingStart),
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    }

    update() {
      const maxScroll = this.list.scrollWidth - this.list.clientWidth;
      if (this.prev) this.prev.disabled = this.list.scrollLeft <= 1;
      if (this.next) this.next.disabled = this.list.scrollLeft >= maxScroll - 1;
    }
  }

  customElements.define('c-scroller', CScroller);
}
